import { Settings } from '@streamr/tv-engine/~/settings';
export class OptimalDelayCalculator {
    static MAX_OPTIMAL_DELAY_MS = 500;
    static MAX_UNDERFLOW_BOOST_MS = 200;
    windowLength = Settings.optimalDelayWindowLength;
    minSamplesToStart = Settings.optimalDelayMinSamplesToStart;
    chunkArrivalTimesMillis = [];
    chunkCounter = 0;
    smoothedDelayMillis;
    lastUnderflowTimeMillis;
    underflowBoostMillis = 0;
    reset() {
        this.chunkArrivalTimesMillis = [];
        this.chunkCounter = 0;
        this.smoothedDelayMillis = undefined;
        this.lastUnderflowTimeMillis = undefined;
        this.underflowBoostMillis = 0;
    }
    addToQueue(time) {
        this.chunkArrivalTimesMillis.push(time);
        if (this.chunkArrivalTimesMillis.length > this.windowLength) {
            this.chunkArrivalTimesMillis.shift();
        }
    }
    addChunkArrivalTimeMillis(chunkArrivalTimeMillis) {
        this.addToQueue(chunkArrivalTimeMillis);
        this.chunkCounter++;
        this.updateSmoothedDelay(chunkArrivalTimeMillis);
    }
    getFrameCount() {
        return this.chunkArrivalTimesMillis.length;
    }
    reportUnderflow() {
        const now = performance.now();
        if (this.lastUnderflowTimeMillis !== undefined &&
            now - this.lastUnderflowTimeMillis < 1000) {
            return;
        }
        this.lastUnderflowTimeMillis = now;
        this.underflowBoostMillis = Math.min(this.underflowBoostMillis + Settings.optimalDelayUnderflowBoostMs, OptimalDelayCalculator.MAX_UNDERFLOW_BOOST_MS);
        if (this.smoothedDelayMillis !== undefined) {
            const raw = this.calculateOptimalDelayMillis();
            if (raw !== undefined) {
                const capped = Math.min(raw, OptimalDelayCalculator.MAX_OPTIMAL_DELAY_MS);
                this.smoothedDelayMillis = Math.max(this.smoothedDelayMillis, capped);
            }
        }
    }
    calculateOptimalDelayMillis() {
        if (this.chunkArrivalTimesMillis.length < this.minSamplesToStart) {
            return undefined;
        }
        const intervals = [];
        for (let i = 1; i < this.chunkArrivalTimesMillis.length; i++) {
            const interval = this.chunkArrivalTimesMillis[i] -
                this.chunkArrivalTimesMillis[i - 1];
            intervals.push(interval);
        }
        if (intervals.length === 0) {
            return undefined;
        }
        intervals.sort((a, b) => a - b);
        const percentileIndex = Math.min(Math.floor(intervals.length * Settings.optimalDelayPercentile), intervals.length - 1);
        const p95Interval = intervals[percentileIndex];
        const safetyMargin = Math.max(Settings.optimalDelayMinSafetyMs, p95Interval * Settings.optimalDelaySafetyFactor);
        return p95Interval + safetyMargin + this.getActiveUnderflowBoost();
    }
    getSmoothedDelayMillis() {
        return this.smoothedDelayMillis;
    }
    getActiveUnderflowBoost() {
        if (this.underflowBoostMillis <= 0) {
            return 0;
        }
        if (this.lastUnderflowTimeMillis === undefined) {
            return 0;
        }
        const elapsed = performance.now() - this.lastUnderflowTimeMillis;
        const decayStartMs = Settings.optimalDelayUnderflowDecaySeconds * 1000;
        if (elapsed < decayStartMs) {
            return this.underflowBoostMillis;
        }
        const decayElapsed = elapsed - decayStartMs;
        const decayRate = Settings.optimalDelayUnderflowBoostMs / 5000;
        const decayed = this.underflowBoostMillis - decayRate * decayElapsed;
        return Math.max(0, decayed);
    }
    updateSmoothedDelay(nowMillis) {
        const raw = this.calculateOptimalDelayMillis();
        if (raw === undefined) {
            return;
        }
        const cappedRaw = Math.min(raw, OptimalDelayCalculator.MAX_OPTIMAL_DELAY_MS);
        if (this.smoothedDelayMillis === undefined) {
            this.smoothedDelayMillis = cappedRaw;
            return;
        }
        if (cappedRaw > this.smoothedDelayMillis) {
            const jump = cappedRaw - this.smoothedDelayMillis;
            if (jump > this.smoothedDelayMillis * 0.5) {
                this.smoothedDelayMillis += jump * 0.5;
            }
            else {
                this.smoothedDelayMillis = cappedRaw;
            }
        }
        else {
            let decreaseRate = Settings.optimalDelayDecreaseRate;
            if (this.smoothedDelayMillis > cappedRaw * 2.0) {
                decreaseRate = 0.5;
            }
            else if (this.smoothedDelayMillis > cappedRaw * 1.5) {
                decreaseRate = 0.3;
            }
            else if (this.smoothedDelayMillis > cappedRaw * 1.2) {
                decreaseRate = 0.15;
            }
            this.smoothedDelayMillis -=
                (this.smoothedDelayMillis - cappedRaw) * decreaseRate;
        }
        if (this.lastUnderflowTimeMillis !== undefined &&
            nowMillis - this.lastUnderflowTimeMillis >
                Settings.optimalDelayUnderflowDecaySeconds * 1000) {
            const autonomousDecreaseMs = Settings.optimalDelayAutonomousDecreasePerSec / 30;
            this.smoothedDelayMillis = Math.max(cappedRaw, this.smoothedDelayMillis - autonomousDecreaseMs);
        }
    }
}
